/** Connects the LSP client to Monaco: diagnostics, completion, hover.
 *
 * Everything here degrades to "no completions" rather than to a broken editor,
 * because a candidate mid-interview cannot debug our language server.
 */
import * as monaco from "monaco-editor";
import { useEffect, useRef, useState } from "react";

import { LspClient, uriFor, type Diagnostic } from "./lsp";

const DEBOUNCE_MS = 300;
const LSP_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 20_000];

// LSP severity 1..4 (Error, Warning, Information, Hint) onto Monaco's own scale.
const SEVERITY: Record<number, monaco.MarkerSeverity> = {
  1: monaco.MarkerSeverity.Error,
  2: monaco.MarkerSeverity.Warning,
  3: monaco.MarkerSeverity.Info,
  4: monaco.MarkerSeverity.Hint,
};

// LSP CompletionItemKind is 1-based in its own order; Monaco's enum does not match.
const COMPLETION_KIND: Record<number, monaco.languages.CompletionItemKind> = {
  1: monaco.languages.CompletionItemKind.Text,
  2: monaco.languages.CompletionItemKind.Method,
  3: monaco.languages.CompletionItemKind.Function,
  4: monaco.languages.CompletionItemKind.Constructor,
  5: monaco.languages.CompletionItemKind.Field,
  6: monaco.languages.CompletionItemKind.Variable,
  7: monaco.languages.CompletionItemKind.Class,
  8: monaco.languages.CompletionItemKind.Interface,
  9: monaco.languages.CompletionItemKind.Module,
  10: monaco.languages.CompletionItemKind.Property,
  11: monaco.languages.CompletionItemKind.Unit,
  12: monaco.languages.CompletionItemKind.Value,
  13: monaco.languages.CompletionItemKind.Enum,
  14: monaco.languages.CompletionItemKind.Keyword,
  15: monaco.languages.CompletionItemKind.Snippet,
  16: monaco.languages.CompletionItemKind.Color,
  17: monaco.languages.CompletionItemKind.File,
  18: monaco.languages.CompletionItemKind.Reference,
  19: monaco.languages.CompletionItemKind.Folder,
  20: monaco.languages.CompletionItemKind.EnumMember,
  21: monaco.languages.CompletionItemKind.Constant,
  22: monaco.languages.CompletionItemKind.Struct,
  23: monaco.languages.CompletionItemKind.Event,
  24: monaco.languages.CompletionItemKind.Operator,
  25: monaco.languages.CompletionItemKind.TypeParameter,
};

function applyDiagnostics(uri: string, diagnostics: Diagnostic[]): void {
  const model = monaco.editor.getModel(monaco.Uri.parse(uri));
  if (!model) return;
  monaco.editor.setModelMarkers(
    model,
    "pyright",
    diagnostics.map((d) => ({
      severity: SEVERITY[d.severity ?? 1] ?? monaco.MarkerSeverity.Error,
      message: d.source ? `${d.message} (${d.source})` : d.message,
      startLineNumber: d.range.start.line + 1,
      startColumn: d.range.start.character + 1,
      endLineNumber: d.range.end.line + 1,
      endColumn: d.range.end.character + 1,
    })),
  );
}

export function useLsp(url: string | null) {
  const clientRef = useRef<LspClient | null>(null);
  const [up, setUp] = useState(false);
  const timers = useRef(new Map<string, number>());

  useEffect(() => {
    if (!url) return;
    const client = new LspClient();
    client.onDiagnostics = applyDiagnostics;
    client.onStatus = setUp;
    clientRef.current = client;

    const providers: monaco.IDisposable[] = [
      monaco.languages.registerCompletionItemProvider("python", {
        triggerCharacters: [".", "(", "[", '"', "'"],
        async provideCompletionItems(model, position) {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };
          try {
            const result = await client.request<{ items?: unknown[] } | unknown[]>(
              "textDocument/completion",
              {
                textDocument: { uri: model.uri.toString() },
                position: { line: position.lineNumber - 1, character: position.column - 1 },
              },
            );
            const items = (Array.isArray(result) ? result : (result?.items ?? [])) as Array<{
              label: string;
              kind?: number;
              detail?: string;
              insertText?: string;
              sortText?: string;
              documentation?: string | { value: string };
            }>;
            return {
              suggestions: items.slice(0, 200).map((item) => ({
                label: item.label,
                kind: COMPLETION_KIND[item.kind ?? 1] ?? monaco.languages.CompletionItemKind.Text,
                insertText: item.insertText ?? item.label,
                detail: item.detail,
                sortText: item.sortText,
                documentation:
                  typeof item.documentation === "string"
                    ? item.documentation
                    : item.documentation?.value,
                range,
              })),
            };
          } catch {
            return { suggestions: [] };
          }
        },
      }),

      monaco.languages.registerHoverProvider("python", {
        async provideHover(model, position) {
          try {
            const result = await client.request<{
              contents?: { value?: string } | string | Array<string | { value: string }>;
            }>("textDocument/hover", {
              textDocument: { uri: model.uri.toString() },
              position: { line: position.lineNumber - 1, character: position.column - 1 },
            });
            const contents = result?.contents;
            const text =
              typeof contents === "string"
                ? contents
                : Array.isArray(contents)
                  ? contents.map((c) => (typeof c === "string" ? c : c.value)).join("\n")
                  : (contents?.value ?? "");
            return text ? { contents: [{ value: text }] } : null;
          } catch {
            return null;
          }
        },
      }),
    ];

    // Reconnect for the same reason the terminal does: a control-plane restart
    // otherwise leaves the editor with stale diagnostics and no completions,
    // looking like pyright simply stopped working.
    let disposed = false;
    let attempt = 0;
    let retry: number | undefined;

    // start() resolves when the connection ends, so this waits for a real
    // disconnection before backing off and trying again.
    async function connect(): Promise<void> {
      while (!disposed) {
        await client.start(url!);
        if (disposed) return;
        const wait = LSP_BACKOFF_MS[Math.min(attempt, LSP_BACKOFF_MS.length - 1)];
        attempt += 1;
        await new Promise<void>((done) => {
          retry = window.setTimeout(done, wait);
        });
      }
    }

    const originalStatus = client.onStatus;
    client.onStatus = (up) => {
      if (up) attempt = 0;
      originalStatus(up);
    };

    void connect();

    return () => {
      disposed = true;
      window.clearTimeout(retry);
      providers.forEach((p) => p.dispose());
      client.stop();
      clientRef.current = null;
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current.clear();
    };
  }, [url]);

  return {
    up,
    didOpen(path: string, text: string) {
      clientRef.current?.didOpen(path, text);
    },
    didClose(path: string) {
      clientRef.current?.didClose(path);
      const model = monaco.editor.getModel(monaco.Uri.parse(uriFor(path)));
      if (model) monaco.editor.setModelMarkers(model, "pyright", []);
    },
    // Every keystroke would re-type-check the file; the debounce is what keeps
    // the editor responsive on a large module.
    didChange(path: string, text: string) {
      const existing = timers.current.get(path);
      if (existing) window.clearTimeout(existing);
      timers.current.set(
        path,
        window.setTimeout(() => {
          clientRef.current?.didChange(path, text);
          timers.current.delete(path);
        }, DEBOUNCE_MS),
      );
    },
  };
}
